import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Package,
  CheckCircle,
  User,
  Calendar,
  FileText,
  ArrowLeft,
  AlertCircle
} from 'lucide-react';

const parseEncomendaFotos = (rawFotos) => {
  if (!rawFotos) {
    return [];
  }

  let fotosBrutas = [];

  if (Array.isArray(rawFotos)) {
    fotosBrutas = rawFotos;
  } else if (typeof rawFotos === 'string') {
    const trimmed = rawFotos.trim();

    if (!trimmed) {
      return [];
    }

    // Se parecer um JSON, tentar fazer o parse
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        fotosBrutas = Array.isArray(parsed) ? parsed : [parsed];
      } catch (error) {
        console.error('Erro ao fazer parse de encomenda_fotos:', error);
        fotosBrutas = [trimmed];
      }
    } else {
      fotosBrutas = [trimmed];
    }
  } else if (typeof rawFotos === 'object') {
    fotosBrutas = [rawFotos];
  }

  const normalizarFoto = (foto) => {
    if (!foto) return null;

    if (typeof foto === 'string') {
      const valor = foto.trim();
      if (!valor) return null;
      if (/^data:image/i.test(valor) || /^https?:\/\//i.test(valor)) {
        return valor;
      }
      return `data:image/jpeg;base64,${valor}`;
    }

    if (typeof foto === 'object') {
      const candidato = foto.uri || foto.url || foto.data || foto.base64;
      if (!candidato) return null;
      if (/^data:image/i.test(candidato) || /^https?:\/\//i.test(candidato)) {
        return candidato;
      }
      return `data:image/jpeg;base64,${candidato}`;
    }

    return null;
  };

  return fotosBrutas
    .map(normalizarFoto)
    .filter(Boolean);
};

const ConfirmarEntregaEncomenda = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [encomenda, setEncomenda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    entregue_para: '',
    observacoes: ''
  });

  const fotosEncomenda = useMemo(
    () => parseEncomendaFotos(encomenda?.encomenda_fotos),
    [encomenda?.encomenda_fotos]
  );

  useEffect(() => {
    loadEncomenda();
  }, [id]);

  const loadEncomenda = async () => {
    try {
      setLoading(true);
      console.log('📦 Carregando encomenda ID:', id);
      const response = await api.get(`/encomendas/${id}`);
      console.log('✅ Response completo:', response.data);
      console.log('📦 Encomenda data:', response.data.data);
      console.log('📦 Encomenda objeto:', response.data.data?.encomenda);
      console.log('📊 Status da encomenda:', response.data.data?.encomenda?.encomenda_status);
      // O backend retorna { data: { encomenda: {...} } }
      setEncomenda(response.data.data?.encomenda || response.data.data);
    } catch (error) {
      console.error('❌ Erro ao carregar encomenda:', error);
      console.error('❌ Response:', error.response);
      setError('Erro ao carregar informações da encomenda');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.entregue_para.trim()) {
      setError('Por favor, informe quem recebeu a encomenda');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await api.put(`/encomendas/${id}`, {
        encomenda_status: 'entregue',
        entregue_para: formData.entregue_para,
        encomenda_observacoes: formData.observacoes,
        encomenda_data_entrega: new Date().toISOString()
      });

      // Redirecionar de volta para a lista de encomendas
      navigate('/encomendas');
    } catch (error) {
      console.error('Erro ao confirmar entrega:', error);
      setError(error.response?.data?.message || 'Erro ao confirmar entrega da encomenda');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusLabel = (status) => {
    const labels = {
      'aguardando': 'Aguardando Retirada',
      'entregue': 'Entregue',
      'devolvida': 'Devolvida',
      'perdida': 'Perdida'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!encomenda) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">
                Encomenda não encontrada
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={() => navigate('/encomendas')}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Encomendas
          </button>
        </div>
      </div>
    );
  }

  if (encomenda.encomenda_status !== 'aguardando') {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">
                Esta encomenda já foi processada (Status: {getStatusLabel(encomenda.encomenda_status)})
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={() => navigate('/encomendas')}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Encomendas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/encomendas')}
            className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Encomendas
          </button>
          
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            Confirmar Entrega de Encomenda
          </h1>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Informações da Encomenda */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-gray-600" />
            Informações da Encomenda
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Código</label>
              <p className="text-gray-900 font-medium">{encomenda.encomenda_codigo}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Tipo</label>
              <p className="text-gray-900">{encomenda.encomenda_tipo}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Destinatário</label>
              <p className="text-gray-900">{encomenda.encomenda_destinatario_nome}</p>
              {encomenda.encomenda_destinatario_telefone && (
                <p className="text-sm text-gray-500">{encomenda.encomenda_destinatario_telefone}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Apartamento</label>
              <p className="text-gray-900">
                {encomenda.apartamento?.apartamento_bloco} - {encomenda.apartamento?.apartamento_numero}
              </p>
              <p className="text-sm text-gray-500">
                {encomenda.apartamento?.apartamento_andar}º andar
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Remetente</label>
              <p className="text-gray-900">{encomenda.encomenda_remetente_nome}</p>
              {encomenda.encomenda_remetente_empresa && (
                <p className="text-sm text-gray-500">{encomenda.encomenda_remetente_empresa}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Data de Recebimento</label>
              <p className="text-gray-900">{formatDateTime(encomenda.encomenda_data_recebimento)}</p>
            </div>

            {encomenda.encomenda_descricao && (
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-500">Descrição</label>
                <p className="text-gray-900">{encomenda.encomenda_descricao}</p>
              </div>
            )}
          </div>
        </div>

        {fotosEncomenda.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Fotos da Encomenda
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fotosEncomenda.map((foto, index) => (
                <div
                  key={`${foto}-${index}`}
                  className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                >
                  <img
                    src={foto}
                    alt={`Foto ${index + 1} da encomenda`}
                    className="w-full h-64 object-contain bg-white"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Formulário de Confirmação de Entrega */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-gray-600" />
            Confirmar Entrega
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entregue para *
              </label>
              <input
                type="text"
                name="entregue_para"
                value={formData.entregue_para}
                onChange={handleInputChange}
                required
                maxLength={100}
                placeholder="Nome da pessoa que recebeu a encomenda"
                className="input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Informe o nome completo de quem está recebendo a encomenda
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                name="observacoes"
                value={formData.observacoes}
                onChange={handleInputChange}
                maxLength={500}
                rows={3}
                placeholder="Observações sobre a entrega (opcional)"
                className="input"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <Calendar className="h-5 w-5 text-blue-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-800">
                    Data e hora da entrega
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    {formatDateTime(new Date().toISOString())}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              <div className="flex">
                <User className="h-5 w-5 text-gray-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-800">
                    Confirmado por
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    {user?.nome || 'Usuário atual'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/encomendas')}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Confirmando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirmar Entrega
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConfirmarEntregaEncomenda;

